'use client';

import { useEffect, useState } from 'react';
import { ICookieConsent } from './interfaces';

import 'vanilla-cookieconsent/dist/cookieconsent.css';
import * as CookieConsent from 'vanilla-cookieconsent';
import Script from 'next/script';
import ruTexts from '../constants/ru.json';
import enTexts from '../constants/en.json';
import dynamic from 'next/dynamic';
import { metricsId } from '../constants/constants';
import YandexMetrica from '../scripts/YandexMetrica';

declare const window: Window & { dataLayer: any };

const updateCookieConsent = () => {
  CookieConsent.showPreferences();
};

const resetCookieConsent = () => {
  CookieConsent.reset(true);
};

const listenForConsent = (state: any, gtagId: string) => {
  window.dataLayer = window.dataLayer || [];
  const pushToDataLayer = (...args: any[]) => window.dataLayer.push(args);
  pushToDataLayer('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
  });

  state.setLoadScript(true);

  pushToDataLayer('js', new Date());
  pushToDataLayer('config', gtagId);

  const updateGtagConsent = () => {
    pushToDataLayer('consent', 'update', {
      ad_storage: CookieConsent.acceptedCategory('advertisement') ? 'granted' : 'denied',
      ad_user_data: CookieConsent.acceptedCategory('advertisement') ? 'granted' : 'denied',
      ad_personalization: CookieConsent.acceptedCategory('advertisement') ? 'granted' : 'denied',
      analytics_storage: CookieConsent.acceptedCategory('analytics') ? 'granted' : 'denied',
      functionality_storage: CookieConsent.acceptedCategory('functional') ? 'granted' : 'denied',
      personalization_storage: CookieConsent.acceptedCategory('functional') ? 'granted' : 'denied',
      security_storage: 'granted',
    });
  };

  const handleConsent = () => {
    updateGtagConsent();
  };

  // window.addEventListener("cc:onConsent", () => {
  //   updateGtagConsent();
  //   console.log("Consent given");
  // });

  // window.addEventListener("cc:onChange", () => {
  //   updateGtagConsent();
  //   console.log("Consent changed");
  // });

  window.addEventListener('cc:onConsent', handleConsent);
  window.addEventListener('cc:onChange', handleConsent);

  // Возвращаем функцию очистки для удаления обработчиков событий
  return () => {
    window.removeEventListener('cc:onConsent', handleConsent);
    window.removeEventListener('cc:onChange', handleConsent);
  };
};

const CookieConsentComponent = ({ locale }: ICookieConsent) => {
  const metricsKeys = locale === 'ru' ? metricsId.ru : metricsId.com;
  const gtagId = metricsKeys.gtag;

  const [loadScript, setLoadScript] = useState(false);

  useEffect(() => {
    /**
     * All config. options available here:
     * https://cookieconsent.orestbida.com/reference/configuration-reference.html
     */
    const cleanup = listenForConsent({ setLoadScript }, gtagId);

    CookieConsent.run({
      guiOptions: {
        consentModal: {
          layout: 'cloud inline',
          position: 'bottom center',
          equalWeightButtons: false,
          flipButtons: false,
        },
      },
      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        // functional: {},
        analytics: {},
        // performance: {},
        // advertisement: {},
      },
      language: {
        default: 'ru',
        autoDetect: 'browser',
        translations: {
          en: enTexts,
          ru: ruTexts,
        },
      },
    });
    return cleanup; // Вызываем функцию очистки при размонтировании компонента
  }, [gtagId]);

  // const YandexMetricaDynamic = dynamic(() => import('../scripts/YandexMetrica'));

  return (
    <>
      {loadScript && (
        <YandexMetrica yaMetric={metricsKeys.yaMetric} />
        // <>
        //   <Script
        //     async
        //     strategy="lazyOnload"
        //     data-cookiecategory="analytics"
        //     src={`https://www.googletagmanager.com/gtag/js?id=${gtagId}`}
        //   />
        //   <Script id="google-analytics" strategy="lazyOnload" data-cookiecategory="analytics">
        //     {`
        //       window.dataLayer = window.dataLayer || [];
        //       function gtag(){dataLayer.push(arguments);}
        //       gtag('js', new Date());

        //       gtag('config', '${gtagId}');
        //   `}
        //   </Script>
        // </>
      )}
    </>
  );
};

export default CookieConsentComponent;

// Да, в v3 vanilla‑cookieconsent всё делается парой строк.
// Сам баннер берёт текст из блока `language.translations.*.consentModal`, а HTML‑теги там разрешены — доки сами приводят пример с якорной ссылкой и даже отдельным `footer` полем для линков ([CookieConsent][1]).

// ### Шаг 1. Дописываем ru.json (или вставляем прямо в конфиг)

// ```jsonc
// // ru.json (фрагмент)
// {
//   "consentModal": {
//     "title": "Мы используем файлы cookie",
//     "description": "Мы используем файлы cookie для улучшения работы сайта. " +
//                    "Также подключена веб‑аналитика <strong>Яндекс.Метрика</strong>. " +
//                    "Подробнее — в нашей <a href=\"/privacy-policy\" target=\"_blank\" rel=\"noopener\">Политике конфиденциальности</a>.",
//     "acceptAllBtn": "Принять всё",
//     "acceptNecessaryBtn": "Только необходимые",
//     "showPreferencesBtn": "Настроить",
//     /* необязательно, но красиво выносит ссылку под кнопки */
//     "footer": "<a href=\"/privacy-policy\" target=\"_blank\" rel=\"noopener\">Политика конфиденциальности</a>"
//   }
// }
// ```

// > ⚙️ Если ruTexts уже импортируется, просто мёржите:
// > `consentModal: { …ruTexts.consentModal, description: '…', footer: '…' }`.

// ### Шаг 2. Обновляем компонент (React / Next .js)

// ```ts
// CookieConsent.run({
//   /* …ваши guiOptions, categories и т.д. */
//   language: {
//     default: 'ru',
//     autoDetect: 'browser',
//     translations: {
//       ru: {                               // 👈 то, что добавили выше
//         consentModal: {
//           ...ruTexts.consentModal,
//           description: ruTexts.consentModal.description +
//             ' Также подключена веб‑аналитика <strong>Яндекс.Метрика</strong>. ' +
//             'Подробнее — в <a href="/privacy-policy" target="_blank" rel="noopener">Политике конфиденциальности</a>.',
//           footer: '<a href="/privacy-policy" target="_blank" rel="noopener">Политика конфиденциальности</a>'
//         }
//       },
//       en: enTexts
//     }
//   }
// });
// ```

// ### (Опционально) грузим сам счётчик Метрики только после согласия

// ```tsx
// {loadScript && (
//   <>
//     {/* Google Analytics как было */}
//     <Script … />

//     {/* Яндекс.Метрика — тоже помечаем data-cookiecategory="analytics" */}
//     <Script id="yandex-metrika" strategy="lazyOnload" data-cookiecategory="analytics">
//       {`
//         (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
//         m[i].l=1*new Date();k=e.createElement(t),a=e.getElementsByTagName(t)[0];
//         k.async=1;k.src=r;a.parentNode.insertBefore(k,a)})
//         (window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");
//         ym(YOUR_METRIKA_ID, "init", {
//           clickmap:true,
//           trackLinks:true,
//           accurateTrackBounce:true,
//           webvisor:true
//         });
//       `}
//     </Script>
//   </>
// )}
// ```

// **Итого:**

// * ссылка на политику и фраза про Яндекс.Метрику рендерятся прямо в баннере;
// * HTML‑якоря в `description`/`footer` поддерживаются движком плагина;
// * скрипт Метрики стартует только после принятия категории `analytics`, точно так же, как Google Analytics.

// [1]: https://cookieconsent.orestbida.com/essential/getting-started.html "Getting Started | CookieConsent"
